import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ShieldCheck, HeartHandshake, Award, Users } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const VALUES = [
  { icon: Award, title: "Licensed & Accredited", body: "Our program is built and delivered under Canadian professional standards, with CARF-aligned processes and LegitScript-certified operations." },
  { icon: Users, title: "Multidisciplinary", body: "Doctors, counsellors, therapists and support staff collaborate on your care — no siloed handoffs, no one-size-fits-all plans." },
  { icon: HeartHandshake, title: "Trauma-Informed", body: "Every clinician on our team is trained to recognise and respond to the trauma that so often lives alongside addiction and mental-health conditions." },
  { icon: ShieldCheck, title: "Confidential & Respectful", body: "Your story is yours. We protect your privacy under PHIPA and treat you as a whole person — not a diagnosis." },
];

const TeamPage = () => {
  const navigate = useNavigate();
  useSEO({
    title: "Our Team — Licensed Addiction & Mental-Health Professionals | New Beginnings Toronto",
    description: "Meet the licensed, multidisciplinary team behind New Beginnings Recovery Home in Toronto: doctors, counsellors and therapists trained in trauma-informed inpatient care.",
    keywords: "addiction treatment team Toronto, licensed rehab staff, multidisciplinary care, trauma-informed therapy",
    canonical: "https://newbeginningsrecovery.ca/about/team",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="team-page" className="bg-sand min-h-screen">
      <Navbar />
      <section className="pt-40 md:pt-48 pb-16">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4">Our Team</p>
            <h1 className="font-serif text-5xl md:text-7xl text-forest tracking-tight leading-[0.98]">
              The people who <span className="italic text-terracotta">walk with you</span>
            </h1>
            <p className="mt-6 text-lg text-ink2 max-w-2xl leading-relaxed">
              A licensed, accredited and multidisciplinary team of doctors, counsellors and therapists —
              dedicated to your well-being every step of the way.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10 grid md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={(i % 2) * 0.08}>
              <div className="h-full rounded-2xl bg-white border border-line p-8 hover:shadow-[0_20px_40px_-16px_rgba(46,106,138,0.28)] transition-shadow">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-terracotta/15 text-terracotta border border-terracotta/25 mb-5">
                  <v.icon className="w-6 h-6" />
                </span>
                <h3 className="font-serif text-2xl text-forest tracking-tight">{v.title}</h3>
                <p className="mt-3 text-ink2 leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-forest text-sand py-20 md:py-28">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02]">Let our team help you begin</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/contact")} className="h-13 px-7 py-6 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold">Get Help Today</Button>
            <a href={CONTACT.phoneHref} className="h-auto px-7 py-4 rounded-full border border-sand/40 text-sand hover:bg-sand hover:text-forest flex items-center justify-center gap-2 font-semibold transition-colors">
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TeamPage;
